/**
 * Aegix One invitation management service for SMS.
 *
 * Migrated to typed `@tiaki/http-client` per ADR 113 §Decision.
 */

import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

// --- Types ---

export interface InvitationStudent {
  public_id: string;
  first_name: string;
  last_name: string;
  grade: string;
  homeroom: string;
}

export interface Invitation {
  public_id: string;
  recipient_email: string;
  recipient_phone: string;
  recipient_name: string;
  role: 'guardian' | 'self';
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  school_name: string;
  invited_by_email: string | null;
  students: InvitationStudent[];
  sent_via: 'email' | 'sms' | 'both';
  is_expired: boolean;
  expires_at: string;
  accepted_at: string | null;
  last_sent_at: string | null;
  created_at: string;
}

export interface InvitationListResponse {
  count: number;
  results: Invitation[];
}

export interface InvitationStats {
  total_invitations: number;
  pending: number;
  accepted: number;
  expired: number;
  revoked: number;
  acceptance_rate: number;
}

export interface CreateInvitationPayload {
  recipient_email: string;
  recipient_phone?: string;
  recipient_name?: string;
  role: 'guardian' | 'self';
  student_ids: string[];
  school_id: string;
  sent_via?: 'email' | 'sms' | 'both';
}

export interface BulkInvitePayload {
  student_ids: string[];
  school_id: string;
  sent_via?: 'email' | 'sms' | 'both';
}

export interface BulkInviteResponse {
  created: number;
  skipped: number;
  detail: string;
}

// --- API Calls ---

/** Create a single invitation. */
export async function createInvitation(
  payload: CreateInvitationPayload,
): Promise<Invitation> {
  const result = await client.POST('/api/v2/roster/invitations/', {
    body: payload as never,
  });
  return unwrap(result) as unknown as Invitation;
}

/** List invitations with optional filters. */
export async function listInvitations(params?: {
  school_id?: string;
  status?: string;
}): Promise<InvitationListResponse> {
  const result = await client.GET('/api/v2/roster/invitations/list/', {
    params: { query: (params ?? {}) as never },
  });
  return unwrap(result) as unknown as InvitationListResponse;
}

/** Get invitation detail. */
export async function getInvitation(publicId: string): Promise<Invitation> {
  const result = await client.GET('/api/v2/roster/invitations/{public_id}/', {
    params: { path: { public_id: publicId } as never },
  });
  return unwrap(result) as unknown as Invitation;
}

/** Resend a pending invitation. */
export async function resendInvitation(
  publicId: string,
): Promise<{ detail: string }> {
  const result = await client.POST(
    '/api/v2/roster/invitations/{public_id}/resend/',
    {
      params: { path: { public_id: publicId } as never },
      body: {} as never,
    },
  );
  return unwrap(result) as { detail: string };
}

/** Revoke a pending invitation. */
export async function revokeInvitation(
  publicId: string,
): Promise<{ detail: string }> {
  const result = await client.POST(
    '/api/v2/roster/invitations/{public_id}/revoke/',
    {
      params: { path: { public_id: publicId } as never },
      body: {} as never,
    },
  );
  return unwrap(result) as { detail: string };
}

/** Bulk invite guardians for a list of students. */
export async function bulkInvite(
  payload: BulkInvitePayload,
): Promise<BulkInviteResponse> {
  const result = await client.POST('/api/v2/roster/invitations/bulk/', {
    body: payload as never,
  });
  return unwrap(result) as unknown as BulkInviteResponse;
}

/** Get invitation acceptance statistics. */
export async function getInvitationStats(
  schoolId?: string,
): Promise<InvitationStats> {
  const result = await client.GET('/api/v2/roster/invitations/stats/', {
    params: {
      query: (schoolId ? { school_id: schoolId } : {}) as never,
    },
  });
  return unwrap(result) as unknown as InvitationStats;
}

// --- Emergency Contacts ---

export interface StudentContact {
  contact_name: string;
  relationship: string;
  email: string;
  phone: string;
}

export interface StudentContactsResponse {
  student_id: string;
  contacts: StudentContact[];
}

/** Get emergency contacts for a student (for invite pre-fill). */
export async function getStudentContacts(
  studentPublicId: string,
): Promise<StudentContactsResponse> {
  const result = await client.GET(
    '/api/v2/roster/invitations/student/{public_id}/contacts/',
    {
      params: { path: { public_id: studentPublicId } as never },
    },
  );
  return unwrap(result) as unknown as StudentContactsResponse;
}
